import React, {memo} from 'react';
import dynamic from 'next/dynamic';
import Image, {StaticImageData} from 'next/image';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MUIButton from '@/components/MUI-Components/MUI-Button';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import {fadeInUp} from '@/animation/scroll-animation';
import {borderRadius, colors, fontSize, fontWeight, gradients} from '@/styles/tokens';
import {nftButtonSx} from '@/styles/nftButtonSx';

const AnimatedComponent = dynamic(() => import('../AnimatedComponent').then((mod) => mod.AnimatedComponent));

interface CTAProps {
    heading: string;
    subheading: string;
    buttonText: string;
    bullets?: string[];
    openForm: () => void;
    /** Omit for a text-only band — the copy then spans the full width. */
    imageSrc?: StaticImageData | string;
    imageAlt?: string;
    /** Optional secondary button — white bg, black text, rendered beside the primary */
    secondaryButtonText?: string;
    onSecondaryClick?: () => void;
    /** Artwork laid over the card in place of the default dark gradient. */
    backgroundImage?: StaticImageData | string;
}

const secondaryBtnSx = {
    bgcolor: colors.background.default,
    color: `${colors.text.primary} !important`,
    fontSize: fontSize[16],
    fontWeight: fontWeight.bold,
    borderRadius: borderRadius.lg,
    border: 'none',
    '&:hover': {
        color: `${colors.text.inverse} !important`,
        border: `1px solid ${colors.text.inverse} !important`,
        bgcolor: 'transparent'
    }
} as const;

const containerSx = {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: borderRadius.xl,
    background: gradients.darkSection
} as const;

// Fills the card behind the copy; the grid is lifted above it with `contentLayerSx`.
const backgroundLayerSx = {position: 'absolute', inset: 0} as const;
const contentLayerSx = {position: 'relative'} as const;

const headingSx = {
    fontWeight: fontWeight.semibold,
    lineHeight: 1.2,
    background: colors.text.inverse,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent'
};

const subheadingSx = {
    opacity: 0.85,
    letterSpacing: '0.01rem'
} as const;

const bulletIconSx = {
    color: colors.text.inverse,
    fontSize: fontSize[20]
} as const;

const bulletTextSx = {
    fontSize: fontSize[16]
} as const;

const secondaryBtnWrapSx = {
    color: colors.text.primary
} as const;

const imageColumnSx = {
    display: {xs: 'none', md: 'flex'}
} as const;

const imageWrapSx = {
    position: 'relative',
    width: '100%',
    height: '100%',
    minHeight: {xs: 250, md: '100%'}
} as const;

const CTA: React.FC<CTAProps> = ({
    heading,
    subheading,
    buttonText,
    bullets,
    openForm,
    imageSrc,
    imageAlt,
    secondaryButtonText,
    onSecondaryClick,
    backgroundImage
}) => {
    return (
        <MUIContainer>
            <Box sx={containerSx}>
                {backgroundImage && (
                    <Box sx={backgroundLayerSx} aria-hidden="true">
                        <Image src={backgroundImage} alt="" fill sizes="(max-width: 1440px) 100vw, 1440px" style={{objectFit: 'cover'}} />
                    </Box>
                )}
                <Grid container alignItems="stretch" sx={contentLayerSx}>
                    <Grid item xs={12} md={imageSrc ? 7 : 12}>
                        <AnimatedComponent variants={fadeInUp}>
                            <Box
                                p={{xs: 3, md: 6}}
                                display="flex"
                                flexDirection="column"
                                justifyContent="center"
                                alignItems="flex-start"
                                height="100%"
                                color={colors.text.inverse}
                            >
                                <Typography
                                    component="p"
                                    fontSize={{xs: fontSize[28], md: fontSize[40]}}
                                    className="font-headers"
                                    sx={headingSx}
                                >
                                    {heading}
                                </Typography>

                                <Typography pt={2} pb={2.5} sx={subheadingSx}>
                                    {subheading}
                                </Typography>

                                <Box display="flex" flexDirection="column" gap={1.5}>
                                    {bullets && bullets.length > 0 && (
                                        <Box display="flex" flexDirection="column" gap={1.5} mb={4}>
                                            {bullets?.map((item, index) => (
                                                <Box key={index} display="flex" alignItems="center" gap={1.5}>
                                                    <CheckCircleIcon sx={bulletIconSx} />
                                                    <Typography variant="body2" sx={bulletTextSx}>
                                                        {item}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>

                                <Box display="flex" flexDirection={{xs: 'column', sm: 'row'}} gap={2} flexWrap="wrap">
                                    <MUIButton
                                        label={buttonText}
                                        aria-label={buttonText}
                                        variant="contained"
                                        size="medium"
                                        endIcon={<ArrowForwardIcon />}
                                        disableRipple={true}
                                        className="animated-button moving-icon-button"
                                        sx={nftButtonSx}
                                        onClick={openForm}
                                    />
                                    {secondaryButtonText && (
                                        <Box sx={secondaryBtnWrapSx}>
                                            <MUIButton
                                                label={secondaryButtonText}
                                                aria-label={secondaryButtonText}
                                                variant="contained"
                                                size="medium"
                                                endIcon={<ArrowForwardIcon />}
                                                disableRipple={true}
                                                className="animated-button moving-icon-button"
                                                sx={secondaryBtnSx}
                                                onClick={onSecondaryClick}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                        </AnimatedComponent>
                    </Grid>

                    {imageSrc && (
                        <Grid item xs={12} md={5} sx={imageColumnSx}>
                            <AnimatedComponent variants={fadeInUp} delay={0.2} style={{width: '100%', height: '100%'}}>
                                <Box sx={imageWrapSx}>
                                    <Image
                                        src={imageSrc}
                                        alt={imageAlt || 'CTA Image'}
                                        fill
                                        style={{objectFit: 'cover'}}
                                        sizes="(max-width: 900px) 100vw, 40vw"
                                        priority
                                    />
                                </Box>
                            </AnimatedComponent>
                        </Grid>
                    )}
                </Grid>
            </Box>
        </MUIContainer>
    );
};

export default memo(CTA);
